import { useCases } from './useCases'
import { TUTORIALS } from '../data/tutorials'
import {
  SCENARIOS,
  getScenarioByCategoryAndCase,
  getScenarioPath,
  resolveCategorySlug,
} from '../data/scenarios'
import type { Scenario } from '../data/scenarios/types'
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_SHARE_META,
  SITE_ORIGIN,
  getShareMetaForPath,
  getShareMetaUrl,
  normalizePathname,
} from './publicShareMeta'
import { FICTIONAL_SCENARIO_DISCLAIMER } from './seoUtils'

const ORGANIZATION = {
  '@type': 'Organization',
  name: 'Livewello',
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/livewello-logo-512.png`,
  description: DEFAULT_SHARE_META.description,
}

function publisher() {
  return {
    '@type': 'Organization',
    name: 'Livewello',
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_ORIGIN}/livewello-logo-512.png`,
    },
  }
}

function scenarioArticleBody(scenario: Scenario): string {
  const beats = [...scenario.beats, ...(scenario.extraBeats ?? [])]
  return beats
    .filter((b) => b.type === 'narrative' || b.type === 'reveal')
    .map((b) => b.body)
    .join('\n\n')
}

function scenarioStructuredData(scenario: Scenario, path: string) {
  const meta = getShareMetaForPath(path)
  const url = getShareMetaUrl(meta)
  const image = meta.ogImage ?? DEFAULT_OG_IMAGE

  return {
    '@context': 'https://schema.org',
    '@type': ['Article', 'MedicalWebPage'],
    headline: scenario.title,
    description: scenario.meta.description,
    url,
    image,
    author: ORGANIZATION,
    publisher: publisher(),
    mainEntityOfPage: url,
    articleBody: scenarioArticleBody(scenario),
    about: {
      '@type': 'MedicalCondition',
      name: scenario.persona.chiefConcern,
    },
    audience: {
      '@type': 'MedicalAudience',
      audienceType: 'Patient',
    },
    specialty: scenario.series,
    abstract: FICTIONAL_SCENARIO_DISCLAIMER,
  }
}

/** JSON-LD structured data blocks for a public route. */
export function getStructuredDataForPath(pathname: string): Record<string, unknown>[] {
  const path = normalizePathname(pathname)
  const meta = getShareMetaForPath(path)
  const url = getShareMetaUrl(meta)

  if (path === '/') {
    return [
      { '@context': 'https://schema.org', ...ORGANIZATION },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Livewello',
        url: SITE_ORIGIN,
        description: DEFAULT_SHARE_META.description,
        publisher: ORGANIZATION,
      },
    ]
  }

  const scenarioMatch = path.match(/^\/scenarios\/([^/]+)\/([^/]+)$/)
  if (scenarioMatch) {
    const [, category, slug] = scenarioMatch
    const resolvedCategory = resolveCategorySlug(category)
    const scenario = resolvedCategory ? getScenarioByCategoryAndCase(resolvedCategory, slug) : undefined
    if (scenario) {
      return [scenarioStructuredData(scenario, path)]
    }
  }

  const tutorialMatch = path.match(/^\/tutorials\/(web|mobile)\/([^/]+)$/)
  if (tutorialMatch) {
    const tutorial = TUTORIALS.find((t) => t.slug === tutorialMatch[2] && t.platform === tutorialMatch[1])
    if (tutorial) {
      return [
        {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: tutorial.title,
          description: tutorial.meta.description,
          url,
          image: tutorial.coverImage.startsWith('http')
            ? tutorial.coverImage
            : `${SITE_ORIGIN}${tutorial.coverImage}`,
          step: tutorial.sections.map((section, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: section.heading,
            text: section.paragraphs.join(' '),
            image: section.image
              ? section.image.src.startsWith('http')
                ? section.image.src
                : `${SITE_ORIGIN}${section.image.src}`
              : undefined,
          })),
        },
      ]
    }
  }

  if (path.startsWith('/tutorials')) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: meta.title,
        description: meta.description,
        url,
      },
    ]
  }

  const howToMatch = path.match(/^\/how-to-use\/([^/]+)\/([^/]+)$/)
  if (howToMatch) {
    const [, categoryId, useCaseId] = howToMatch
    const useCase = useCases.find((u) => u.id === useCaseId && u.category === categoryId)
    if (useCase) {
      return [
        {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: useCase.title,
          description: useCase.description,
          url,
          step: useCase.steps.map((step) => ({
            '@type': 'HowToStep',
            position: step.number,
            name: step.title,
            text: step.description,
          })),
        },
      ]
    }
  }

  if (path.startsWith('/how-to-use')) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: meta.title,
        description: meta.description,
        url,
      },
    ]
  }

  if (path.startsWith('/scenarios')) {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: meta.title,
        description: meta.description,
        url,
      },
    ]
  }

  if (path === '/docs/ai') {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        name: meta.title,
        description: meta.description,
        url,
        author: ORGANIZATION,
        publisher: publisher(),
      },
    ]
  }

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: meta.title,
      description: meta.description,
      url,
    },
  ]
}

export function getCanonicalScenarioPaths(): string[] {
  return SCENARIOS.map(getScenarioPath)
}
